import { useEffect, useRef } from "react";

import { isHttpError } from "../../../../../lib/query/errors";
import { isCompleteZip, useZipLookup } from "../api/useZipLookup";
import type { ZipLookupStatus } from "../fields/useZipAutofill";

/** City/State to write: only the keys present are filled; a failed lookup clears both. */
export interface ZipAutofillResult {
  city?: string;
  state?: string;
}

/**
 * Looks up City/State once a complete 5-digit ZIP is entered and hands the
 * result to `onFill` — the Call Script modals keep plain state rather than
 * RHF, so this stands in for the Residence tab's useZipAutofill. Fills once
 * per distinct ZIP so it never overrides a manual City/State edit; editing the
 * ZIP back to something incomplete clears the City/State it filled and re-arms it.
 * @param {string} zip - The ZIP input's current value.
 * @param {boolean} enabled - False to skip the lookup (e.g. while mailing mirrors physical).
 * @param {(result: ZipAutofillResult) => void} onFill - Writes the looked-up City/State.
 * @returns {ZipLookupStatus} Status for ZipLookupMessage.
 */
export function useCallScriptZipAutofill(
  zip: string,
  enabled: boolean,
  onFill: (result: ZipAutofillResult) => void
): ZipLookupStatus {
  const zipForLookup = enabled && isCompleteZip(zip) ? zip : undefined;
  const lookup = useZipLookup(zipForLookup);
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const lastFilledZipReference = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!zipForLookup) {
      // Editing a filled ZIP back down to something incomplete never triggers
      // a lookup, so nothing else would clear the previous ZIP's City/State.
      // A disabled block (mailing mirroring physical) keeps its values.
      const hadFill = lastFilledZipReference.current !== undefined;
      lastFilledZipReference.current = undefined;
      if (enabled && hadFill) {
        onFill({ city: "", state: "" });
      }
      return;
    }
    if (lastFilledZipReference.current === zipForLookup) {
      return;
    }
    if (lookup.data) {
      lastFilledZipReference.current = zipForLookup;
      onFill({
        city: lookup.data.city?.toUpperCase() || undefined,
        state: lookup.data.state?.toUpperCase() || undefined,
      });
      return;
    }
    // A not-found/failed lookup means City/State can't be trusted for this
    // ZIP — clear them rather than keep the previous ZIP's values.
    if (lookup.isError) {
      lastFilledZipReference.current = zipForLookup;
      onFill({ city: "", state: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onFill is recreated each render; only the lookup result should retrigger this
  }, [zipForLookup, enabled, lookup.data, lookup.isError]);

  if (!zipForLookup) {
    return "idle";
  }
  if (lookup.isFetching) {
    return "loading";
  }
  if (lookup.isError) {
    return isHttpError(lookup.error, 404) ? "not-found" : "error";
  }
  return "idle";
}
